import type { ListCardCell } from 'components/list-card';
import type { QuickBooksLtlRussInvoiceLink } from 'store/api/quickbooks/ltl-russ-invoice-link-api';

// T-F07: the "⋯" row menu on the stock order / stock transfer lists. Every
// item reads the record itself, except "View Russ invoice", whose matched
// PDF link comes from the worker (FIX-ADMIN-RUSS-PDF: GET
// /ltl/russ-invoice-link, the record's link only, so Administrative may
// read it too; same match as the freight view's invoice.pdf.receiptUrl).

/** Trimmed text; a value with no letter or digit (e.g. "-") is not set. */
export const textOrNull = (value?: string | null) => {
  const text = typeof value === 'string' ? value.trim() : '';
  return /[a-z0-9]/i.test(text) ? text : null;
};

const withScheme = (link: string) =>
  /^[a-z][a-z0-9+.-]*:/i.test(link) ? link : `https://${link}`;

// http(s) only: a stored link is never opened as javascript:, data:, ...
export const safeHttpUrl = (value?: string | null) => {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!text) {
    return null;
  }
  try {
    const url = new URL(withScheme(text));
    return url.protocol === 'http:' || url.protocol === 'https:'
      ? url.toString()
      : null;
  } catch {
    return null;
  }
};

const TRACKING_PLACEHOLDER = /\{(tracking|trackingNumber|tracking_number)\}/i;

interface KnownCarrier {
  // matched by the carrier name (Settings -> Carriers) or its link's domain
  name: RegExp;
  domains: string[];
  // false: the page's number parameter is not confirmed, so the number is
  // also copied, ready to paste
  confirmed: boolean;
  // the carrier's own tracking page for one number
  url: (number: string) => string;
}

/**
 * Owner 2026-10-15: "Track shipment" opens the carrier's page straight to the
 * tracking. Carriers on Production/Dev (read 2026-10-15): Forward Air, UPS,
 * FedEx. Forward Air's page reads `numbers` (its tracking page script);
 * UPS / FedEx / USPS / DHL use their public tracking links. The LTL carriers
 * below are not in the carrier list yet: their links are best known and the
 * number is copied as well.
 */
export const KNOWN_CARRIERS: KnownCarrier[] = [
  {
    confirmed: true,
    name: /forward\s*air/i,
    domains: ['forwardair.com'],
    url: (n) => `https://www.forwardair.com/tracking?numbers=${n}`,
  },
  {
    confirmed: true,
    domains: ['ups.com'],
    name: /^ups\b|united parcel/i,
    url: (n) => `https://www.ups.com/track?loc=en_US&tracknum=${n}`,
  },
  {
    confirmed: true,
    name: /fed\s*ex/i,
    domains: ['fedex.com'],
    url: (n) => `https://www.fedex.com/fedextrack/?trknbr=${n}`,
  },
  {
    confirmed: true,
    domains: ['usps.com'],
    name: /\busps\b|postal service/i,
    url: (n) => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${n}`,
  },
  {
    confirmed: true,
    name: /\bdhl\b/i,
    domains: ['dhl.com'],
    url: (n) =>
      `https://www.dhl.com/us-en/home/tracking/tracking-express.html?submit=1&tracking-id=${n}`,
  },
  {
    confirmed: false,
    domains: ['odfl.com'],
    name: /old\s*dominion|\bodfl\b/i,
    url: (n) =>
      `https://www.odfl.com/us/en/tools/trace-track-ltl-freight/trace.html?proNumbers=${n}`,
  },
  {
    confirmed: false,
    name: /\bestes\b/i,
    domains: ['estes-express.com'],
    url: (n) =>
      `https://www.estes-express.com/myestes/shipment-tracking/?type=PRO&query=${n}`,
  },
  {
    confirmed: false,
    name: /\bxpo\b/i,
    domains: ['xpo.com'],
    url: (n) => `https://www.xpo.com/track/?pro=${n}`,
  },
  {
    confirmed: false,
    name: /\bsaia\b/i,
    domains: ['saia.com'],
    url: (n) => `https://www.saia.com/track/details;pro=${n}`,
  },
  {
    confirmed: false,
    domains: ['rlcarriers.com'],
    name: /^r\s*(\+|&|and)?\s*l\b|\brl carriers\b/i,
    url: (n) =>
      `https://www.rlcarriers.com/freight/shipping/shipment-tracing?pro=${n}&docType=PRO&source=web`,
  },
];

const linkHost = (link: string) => {
  const url = safeHttpUrl(link);
  return url ? new URL(url).hostname.toLowerCase() : '';
};

const knownCarrier = (name: string, link: string) => {
  const host = link ? linkHost(link) : '';
  return KNOWN_CARRIERS.find(
    (carrier) =>
      (name && carrier.name.test(name)) ||
      (host &&
        carrier.domains.some(
          (domain) => host === domain || host.endsWith(`.${domain}`)
        ))
  );
};

export interface TrackingTarget {
  url: string;
  // also copy the tracking # to paste on the page
  copy: boolean;
  // the page opens on this tracking # (false: the carrier's own page)
  withNumber: boolean;
}

/**
 * The carrier's tracking page for a tracking #, in this order:
 * 1. the carrier link (Settings -> Carriers, "Link to Carrier Website") with
 *    a {tracking} / {trackingNumber} placeholder, or ending in "=" (a query
 *    waiting for the number): the number goes there;
 * 2. a known carrier (by name or link domain): its tracking link;
 * 3. any other carrier link: opens as it is, and the number is copied.
 */
export const trackingTarget = (
  carrier?: { name?: string | null; link?: string | null } | null,
  trackingNumber?: string | null
): TrackingTarget | null => {
  const number = textOrNull(trackingNumber);
  const link = typeof carrier?.link === 'string' ? carrier.link.trim() : '';
  const name = typeof carrier?.name === 'string' ? carrier.name.trim() : '';
  if (!number || (!link && !name)) {
    return null;
  }
  const encoded = encodeURIComponent(number);
  if (TRACKING_PLACEHOLDER.test(link)) {
    const url = safeHttpUrl(link.replace(TRACKING_PLACEHOLDER, encoded));
    return url ? { url, copy: false, withNumber: true } : null;
  }
  if (link.endsWith('=')) {
    const url = safeHttpUrl(`${link}${encoded}`);
    return url ? { url, copy: false, withNumber: true } : null;
  }
  const known = knownCarrier(name, link);
  if (known) {
    return {
      withNumber: true,
      copy: !known.confirmed,
      url: known.url(encoded),
    };
  }
  const url = link ? safeHttpUrl(link) : null;
  return url ? { url, copy: true, withNumber: false } : null;
};

/** The Russ PDF matched to this record (T-L03), http(s) only, if any. */
export const russPdfUrl = (link?: QuickBooksLtlRussInvoiceLink | null) => {
  const url = link?.receiptUrl || '';
  return /^https?:\/\//i.test(url) ? url : null;
};

// Desktop width of the Status column on both lists: its select shows the
// longest status label in full ("Exception", 57px of text beside the search
// and chevron icons); the other columns share the rest equally.
export const STATUS_COLUMN = '152px';

/**
 * Tablet / phone grid placement for a list card whose last value is the row
 * menu: every other cell keeps its place, the menu goes to the last column
 * (right edge of the card).
 */
export const rowMenuCells = (count: number): ListCardCell[] => [
  ...Array.from({ length: count - 1 }, () => ({ col: 'auto', row: 'auto' })),
  { row: 'auto', col: '-2 / -1' },
];
